import { APITagAttributes, ArdoqId, UnpersistedEntity } from '@ardoq/api-types';
import {
  api,
  crudApi,
  fetchEntities,
  fetchEntity,
  fetchJson,
  toRequestOptions,
  Method,
} from './api';
import { isArdoqError, isSuccessResponse } from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import { ApiResponse } from './types';

const fetchTags = (tagIds: ArdoqId[]): ApiResponse<APITagAttributes[]> => {
  return Promise.all(tagIds.map(id => tagApi.fetchTag(id))).then(results => {
    results.forEach(result => {
      // A 404 means the saved perspective references a tag that has since been
      // deleted. The stale reference is expected and the result is filtered out
      // below, so only surface genuinely unexpected failures.
      if (isArdoqError(result) && !api.isNotFound(result)) {
        logError(
          result,
          'Could not fetch tag for selected saved perspective in perspective editor'
        );
      }
    });
    return results.filter(tag => isSuccessResponse(tag));
  });
};

const createTag = (
  tag: UnpersistedEntity<APITagAttributes>
): ApiResponse<APITagAttributes> => {
  const request = new Request('/api/tag', toRequestOptions(Method.POST, tag));
  return fetchJson<APITagAttributes>(request);
};

const updateTag = (tag: APITagAttributes): ApiResponse<APITagAttributes> => {
  const request = new Request(
    `/api/tag/${tag._id}`,
    toRequestOptions(Method.PUT, tag)
  );
  return fetchJson<APITagAttributes>(request);
};

const deleteTag = (tagId: ArdoqId): ApiResponse<void> => {
  const request = new Request(
    `/api/tag/${tagId}`,
    toRequestOptions(Method.DELETE, null)
  );
  return fetchJson<void>(request);
};

export const tagApi = {
  ...crudApi<APITagAttributes>('tag'),
  createTag,
  updateTag,
  deleteTag,
  fetchTag: fetchEntity<APITagAttributes>('tag'),
  fetchAll: fetchEntities<APITagAttributes>('tag'),
  fetchTags,
};
